import * as React from "react";
import { SyndeticsReviews } from "./syndetics/SyndeticsReviews";
import {
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useOutletContext,
} from "react-router-dom";
import { LoaderData } from "../account/MyAccount";
import { useLanguage } from "../../Language";
import {
  LocationConfigurationHolder,
  useLocationConfiguration,
} from "../../LocationConfiguration";
import AddIcon from "@mui/icons-material/Add";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useAccount } from "../../Account";
import axios from "axios";
import { ReviewService, ReviewView } from "../../ReviewService";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRedirectToDefaultTab } from "../resource/ResourcePage";
import { useToast } from "../../../contexts/ToastContext";
import { useReviewDialog } from "../../../contexts/ReviewDialogContext";
import { RatingHighContrast } from "../../rate-and-review/RatingHighContrast";
import {
  getResourceByHostRecordId,
  getResourceById,
} from "../resource/getResourceById";
import { SyndeticsConfigHolder } from "../../SyndeticsConfig";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
const REVIEW_PAGE_SIZE = 5;

export async function reviewsPageLoader({ params }: LoaderFunctionArgs) {
  const titleId = params.titleId;
  const hostRecordId = params.hostRecordId;
  const data = !!titleId
    ? await getResourceById(titleId)
    : await getResourceByHostRecordId(hostRecordId);

  const basePath = !!titleId
    ? `/resource/${titleId}`
    : `/hostrecord/${hostRecordId}`;

  if (data.rtype !== 1) {
    return redirect(`${basePath}/details`);
  }
  const locationConfiguration =
    await LocationConfigurationHolder.fetchLocationConfiguration();

  const { displaySettings } = locationConfiguration.pacSettings ?? {};
  const { showUserReviewsView } = displaySettings ?? {};

  const { hasReviewsTabEnrichments } = await SyndeticsConfigHolder.getData();

  if (!hasReviewsTabEnrichments && !showUserReviewsView) {
    const defaultTabPath = await getRedirectToDefaultTab(data);
    return redirect(`${basePath}/${defaultTabPath}`);
  }

  try {
    const { totalHits, reviews } = await ReviewService.getReviews(
      data.id,
      0,
      REVIEW_PAGE_SIZE
    );

    return { totalHits, reviews };
  } catch (e) {
    throw redirect("/404");
  }
}

// Improvement extract confirmation to a generic dialog context

export const ReviewsPage: React.FC = () => {
  const { totalHits, reviews: initialReviews } = useLoaderData() as LoaderData<
    typeof reviewsPageLoader
  >;
  const resource = useOutletContext<ResourceDWR>();
  const { translate } = useLanguage();
  const { showToast } = useToast();
  const { showReviewDialog } = useReviewDialog();
  const { isAdmin } = useAccount();
  const { locationConfiguration } = useLocationConfiguration();
  const { showUserReviewsView } =
    locationConfiguration?.pacSettings?.displaySettings || {};

  const [reviews, setReviews] = React.useState<ReviewView[]>(
    initialReviews || []
  );
  const [count, setCount] = React.useState<number>(totalHits);

  const [deleteReviewId, setDeleteReviewId] = React.useState<number | null>(
    null
  );

  const handleDeclineDelete = () => {
    setDeleteReviewId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.post<void>(`/reviews/${deleteReviewId}/remove`);
      setReviews(reviews.filter((r) => r.reviewId !== deleteReviewId));
      setCount(count - 1);
      showToast(translate("successfulReviewRemoval"), "success");
    } catch (e) {
      console.error(e);
      showToast(translate("errorRemovingReview"), "error");
    } finally {
      setDeleteReviewId(null);
    }
  };
  const onClickRemove = async (reviewId: number) => {
    if (!isAdmin) {
      return;
    }
    setDeleteReviewId(reviewId);
  };

  const onClickShowMore = async () => {
    const loaded = reviews.length;
    // Improvement disable show more button add loading indicator
    try {
      const { reviews: page, totalHits } = await ReviewService.getReviews(
        resource.id,
        loaded,
        REVIEW_PAGE_SIZE
      );
      setReviews([...reviews, ...page]);
      setCount(totalHits);
    } catch (e: unknown) {}
  };

  return (
    <>
      {showUserReviewsView && (
        <>
          <Dialog open={!!deleteReviewId} onClose={handleDeclineDelete}>
            <DialogTitle>{translate("areYouSureTitle")}</DialogTitle>
            <DialogContent>
              {translate("areYouSureYouWantToRemoveThisReview")}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeclineDelete}>
                {translate("cancel")}
              </Button>
              <Button type="submit" onClick={handleConfirmDelete}>
                {translate("ok")}
              </Button>
            </DialogActions>
          </Dialog>
          <Accordion defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{translate("borrowerReviews")}</Typography>
                <Chip label={count} />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack gap={2}>
                {reviews.length === 0 && (
                  <Box>{translate("noBorrowerReviews")}</Box>
                )}
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={async () => {
                      const result = await showReviewDialog(resource);
                      if (result) {
                        const { success, approved, blocked } = result;
                        if (success && approved && !blocked) {
                          // reviewId is not returned on CreateReviewResponse so we need to re-request
                          const size =
                            reviews.length > REVIEW_PAGE_SIZE
                              ? reviews.length
                              : REVIEW_PAGE_SIZE;
                          const { reviews: page, totalHits } =
                            await ReviewService.getReviews(
                              resource.id,
                              0,
                              size
                            );
                          setReviews(page);
                          setCount(totalHits);
                        }
                      }
                    }}
                  >
                    {translate("addNewReview")}
                  </Button>
                </Box>
                {(reviews || []).map(
                  ({ reviewId, title, name, description, rating }) => (
                    <Box key={`review_${reviewId}`}>
                      <h5>
                        {title}{" "}
                        {isAdmin && (
                          <Button
                            variant="text"
                            onClick={() => onClickRemove(reviewId)}
                          >
                            {translate("removeReview")}
                          </Button>
                        )}
                      </h5>
                      <h6>
                        <Stack direction={"row"} spacing={1}>
                          <RatingHighContrast
                            value={rating}
                            readOnly
                            precision={0.5}
                          />
                          <Box>
                            {translate("by")} {name}
                          </Box>
                        </Stack>
                      </h6>
                      <p>{description}</p>
                    </Box>
                  )
                )}
                {reviews.length < count && (
                  <Button onClick={onClickShowMore}>
                    {translate("showMoreReviews")}
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </>
      )}
      <SyndeticsReviews resource={resource}></SyndeticsReviews>
    </>
  );
};
